import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { Project } from '../types';
import mouseIcon from '../icon/mouse.svg';
import A1 from '../icon/A1.jpg';
import A2 from '../icon/A2.jpg';
import ScrollToTop from '../components/ScrollToTop';

interface Banner {
  id: number;
  title: string;
  title_en?: string;
  subtitle: string;
  subtitle_en?: string;
  description: string;
  description_en?: string;
  image_url: string;
  link: string;
}

const Home: React.FC = () => {
  const { i18n } = useTranslation();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentBanner, setCurrentBanner] = useState(0);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [nextBanner, setNextBanner] = useState(1);
  
  // Refs for lazy loading
  const projectsRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await api.get('/banners');
        setBanners(response.data);
      } catch (error) {
        console.error('Error fetching banners:', error);
        // 如果没有banner数据，创建一个默认的
        if (banners.length === 0) {
          setBanners([{
            id: 1,
            title: i18n.language === 'en' ? 'China Construction Technology Exhibition' : '中国建筑科技展',
            subtitle: 'Embrace The Future',
            description: i18n.language === 'en' 
              ? 'We develop construction technology solutions to help enterprises improve efficiency. We "promote innovation", invest heavily in R&D, and are committed to developing "quality solutions" to drive business growth.'
              : '我们开发和建筑科技解决方案，让企业提高效率。我们"推动创新"，大力投资研发，致力于开发"优质方案"，驱动商业增长。',
            image_url: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80',
            link: '/projects'
          }]);
        }
      }
    };

    const fetchProjects = async () => {
      try {
        const response = await api.get('/projects/featured');
        // 只获取前8个推荐项目用于展示
        setProjects(response.data.slice(0, 8));
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };

    fetchBanners();
    fetchProjects();
  }, [i18n.language]);

  useEffect(() => {
    if (banners.length <= 1) return;
    
    // 重置进度条
    setProgress(0);
    
    // 创建进度条动画
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          // 开始切换动画
          const nextIndex = (currentBanner + 1) % banners.length;
          
          // 立即更新状态，避免延迟
          setNextBanner(nextIndex);
          setIsTransitioning(true);
          setProgress(0); // 重置进度条
          
          // 延迟更新当前banner索引，等待文字淡出后再更新
          setTimeout(() => {
            setCurrentBanner(nextIndex);
          }, 400); // 等待文字淡出完成
          
          // 等待动画完成后重置过渡状态
          setTimeout(() => {
            setIsTransitioning(false);
          }, 1000);
          
          return 0;
        }
        return prev + (100 / 40); // 4秒内完成，每100ms增加2.5%
      });
    }, 100);

    return () => clearInterval(progressInterval);
  }, [banners.length, currentBanner]);

  // Add intersection observer for lazy loading sections
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const handleIntersection = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Trigger loading of images in this section
          const section = entry.target.getAttribute('data-section');
          if (section) {
            // Stop observing once it's visible
            observer.unobserve(entry.target);
          }
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, observerOptions);
    
    if (projectsRef.current) {
      observer.observe(projectsRef.current);
    }
    
    if (contactRef.current) {
      observer.observe(contactRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Helper function to get localized text based on current language
  const getLocalizedText = (zh: string, en: string | undefined) => {
    if (i18n.language === 'en' && en) {
      return en;
    }
    return zh;
  };

  // 默认联系我们区域图片
  const contactImages = {
    office: A1,
    meeting: A2
  };

  return (
    <div className="min-h-screen bg-gray-50 font-PingFang">
      {/* Back to top button */}
      <ScrollToTop />
      
      {/* Banner Section */}
      <div 
        className="relative h-screen w-full overflow-hidden pt-[80px]"
        onMouseEnter={() => setShowScrollIndicator(true)}
        onMouseLeave={() => setShowScrollIndicator(false)}
      >
        {banners.length > 0 ? (
          <>
            {/* 当前图片 */}
            <div
              className="absolute inset-0"
              style={{ 
                transform: isTransitioning ? 'translateX(-100%)' : 'translateX(0)',
                transition: isTransitioning ? 'transform 1000ms cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
                willChange: 'transform'
              }}
            >
              {/* 整个banner区域可点击的链接 - 放在最上层 */}
              <Link 
                to={banners[currentBanner].link}
                className="absolute inset-0 z-20 cursor-pointer"
                aria-label={getLocalizedText(banners[currentBanner].title, banners[currentBanner].title_en)}
              />
              
              {/* Background Image with Overlay */}
              <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${banners[currentBanner].image_url})` }}
              />
              <div className="absolute inset-0 bg-black bg-opacity-40" />
            </div>

            {/* 下一张图片 */}
            <div
              className="absolute inset-0"
              style={{ 
                transform: isTransitioning ? 'translateX(0)' : 'translateX(100%)',
                transition: isTransitioning ? 'transform 1000ms cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
                willChange: 'transform'
              }}
            >
              {/* 整个banner区域可点击的链接 - 放在最上层 */}
              <Link 
                to={banners && banners.length > 0 && nextBanner < banners.length ? banners[nextBanner].link : '#'}
                className="absolute inset-0 z-20 cursor-pointer"
                aria-label={banners && banners.length > 0 && nextBanner < banners.length ? 
                  getLocalizedText(banners[nextBanner].title, banners[nextBanner].title_en) : ''}
              />
              
              {/* Background Image with Overlay */}
              <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: banners && banners.length > 0 && nextBanner < banners.length ? `url(${banners[nextBanner].image_url})` : 'none' }}
              />
              <div className="absolute inset-0 bg-black bg-opacity-40" />
            </div>
            
            {/* 文字内容 - 当前banner */}
            <div 
              className="absolute inset-0 z-10 pointer-events-none"
              style={{
                opacity: isTransitioning ? 0 : 1,
                transition: 'opacity 400ms ease-out'
              }}
            >
              {/* Content Container - Full Height Flex Column */}
              <div className="relative h-full flex flex-col pointer-events-none">
                {/* Top Content Container */}
                <div className="mx-auto w-full max-w-[2000px] px-4 md:px-12 pointer-events-none">
                  {/* Top Content */}
                  <div className="text-white max-w-3xl" style={{ marginTop: '20vh' }}>
                    <div className="w-full h-0.5 bg-white mb-4 md:mb-8" />
                    <h2 className="text-base md:text-lg font-normal mb-2 md:mb-4">{getLocalizedText(banners[currentBanner].subtitle, banners[currentBanner].subtitle_en)}</h2>
                    <h1 className="text-3xl md:text-5xl font-bold mb-4 md:mb-6">{getLocalizedText(banners[currentBanner].title, banners[currentBanner].title_en)}</h1>
                    <p className="text-sm md:text-base leading-relaxed opacity-90 whitespace-pre-line">{getLocalizedText(banners[currentBanner].description, banners[currentBanner].description_en)}</p>
                  </div>
                </div>

                {/* Bottom Content */}
                <div className="absolute bottom-8 md:bottom-12 left-0 w-full">
                  <div className="mx-auto w-full max-w-[2000px] px-4 md:px-12 flex justify-between items-center">
                    <Link
                      to={banners[currentBanner].link}
                      className="inline-block bg-white text-gray-900 px-6 md:px-12 py-2 md:py-4 rounded shadow-lg hover:bg-opacity-90 transition-all text-xs md:text-sm font-medium pointer-events-auto z-30"
                    >
                      {i18n.language === 'en' ? 'View More' : '查看更多'}
                    </Link>
                    
                    {/* Banner Navigation Dots */}
                    <div className="flex items-center pointer-events-auto z-30">
                      {banners.map((_, idx) => (
                        <div key={idx} className="flex items-center">
                          <span className={`text-sm md:text-base font-medium ${idx === currentBanner ? 'text-white' : 'text-white/50'} mr-2 md:mr-4 transition-colors duration-200`}>
                            {String(idx + 1).padStart(2, '0')}
                          </span>
                          {idx === currentBanner && (
                            <div className="relative w-10 md:w-20 h-[2px] mx-2 md:mx-4">
                              <div className="absolute inset-0 bg-white/30" />
                              <div 
                                className="absolute inset-0 bg-white"
                                style={{ 
                                  width: `${progress}%`,
                                  transition: 'width 100ms linear'
                                }} 
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* 文字内容 - 下一个banner */}
            <div 
              className="absolute inset-0 z-10 pointer-events-none" 
              style={{
                opacity: isTransitioning ? 1 : 0,
                transition: 'opacity 600ms ease-in',
                transitionDelay: '400ms'
              }}
            >
              {/* Content Container - Full Height Flex Column */}
              <div className="relative h-full flex flex-col pointer-events-none">
                {/* Top Content Container */}
                <div className="mx-auto w-full max-w-[2000px] px-4 md:px-12 pointer-events-none">
                  {/* Top Content */}
                  <div className="text-white max-w-3xl" style={{ marginTop: '20vh' }}>
                    <div className="w-full h-0.5 bg-white mb-4 md:mb-8" />
                    <h2 className="text-base md:text-lg font-normal mb-2 md:mb-4">{banners && banners.length > 0 && nextBanner < banners.length ? getLocalizedText(banners[nextBanner].subtitle, banners[nextBanner].subtitle_en) : ''}</h2>
                    <h1 className="text-3xl md:text-5xl font-bold mb-4 md:mb-6">{banners && banners.length > 0 && nextBanner < banners.length ? getLocalizedText(banners[nextBanner].title, banners[nextBanner].title_en) : ''}</h1>
                    <p className="text-sm md:text-base leading-relaxed opacity-90 whitespace-pre-line">{banners && banners.length > 0 && nextBanner < banners.length ? getLocalizedText(banners[nextBanner].description, banners[nextBanner].description_en) : ''}</p>
                  </div>
                </div>

                {/* Bottom Content */}
                <div className="absolute bottom-8 md:bottom-12 left-0 w-full">
                  <div className="mx-auto w-full max-w-[2000px] px-4 md:px-12 flex justify-between items-center">
                    <Link
                      to={banners && banners.length > 0 && nextBanner < banners.length ? banners[nextBanner].link : '#'}
                      className="inline-block bg-white text-gray-900 px-6 md:px-12 py-2 md:py-4 rounded shadow-lg hover:bg-opacity-90 transition-all text-xs md:text-sm font-medium pointer-events-auto z-30"
                    >
                      {i18n.language === 'en' ? 'View More' : '查看更多'}
                    </Link>
                    
                    {/* Banner Navigation Dots */}
                    <div className="flex items-center pointer-events-auto z-30">
                      {banners.map((_, idx) => (
                        <div key={idx} className="flex items-center">
                          <span className={`text-sm md:text-base font-medium ${idx === nextBanner ? 'text-white' : 'text-white/50'} mr-2 md:mr-4 transition-colors duration-200`}>
                            {String(idx + 1).padStart(2, '0')}
                          </span>
                          {idx === nextBanner && (
                            <div className="relative w-10 md:w-20 h-[2px] mx-2 md:mx-4">
                              <div className="absolute inset-0 bg-white/30" />
                              <div 
                                className="absolute inset-0 bg-white"
                                style={{ 
                                  width: `${progress}%`,
                                  transition: 'width 100ms linear'
                                }} 
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
            <div className="text-white text-center">
              <h1 className="text-4xl font-bold mb-4">{i18n.language === 'en' ? 'Loading...' : '加载中...'}</h1>
            </div>
          </div>
        )}

        {/* Scroll Indicator */}
        <div className={`absolute left-1/2 -translate-x-1/2 bottom-8 md:bottom-12 z-20 transition-opacity duration-300 ${
          showScrollIndicator ? 'opacity-100' : 'opacity-0'
        }`}>
          <img src={mouseIcon} alt="scroll" className="w-6 md:w-8 h-6 md:h-8 animate-bounce" />
        </div>
      </div>

      {/* Projects Grid Section */}
      <section ref={projectsRef} data-section="projects" className="py-12 md:py-20 bg-white">
        <div className="mx-auto px-4 md:px-12 max-w-[2000px]">
          {/* Header with underline */}
          <div className="relative mb-12 md:mb-24">
            <div className="flex justify-between items-center">
              <Link 
                to="/projects" 
                className="text-2xl md:text-3xl font-bold flex items-center hover:text-gray-700 transition-colors"
              >
                <span>{i18n.language === 'en' ? 'PROJECTS' : '重点案例'}</span>
                <span className="ml-2 transform group-hover:translate-x-1 transition-transform">→</span>
              </Link>
            </div>
            <div className="absolute -bottom-4 md:-bottom-6 left-0 right-0 h-[1px] bg-gray-300" />
          </div>

          {/* Projects Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
            {projects.length > 0 ? (
              projects.map((project) => (
                <div key={project.id} className="group">
                  <Link to={`/projects/${project.id}`} className="block relative aspect-[4/3] overflow-hidden mb-3 md:mb-4">
                    <img
                      src={project.image_url}
                      alt={getLocalizedText(project.title, project.title_en)}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/400x300?text=Project+Image';
                      }}
                      loading="lazy"
                    />
                  </Link>
                  <h3 className="text-lg md:text-xl font-bold mb-1 md:mb-2">{getLocalizedText(project.title, project.title_en)}</h3>
                  <p className="text-sm md:text-base text-gray-500 line-clamp-2 mb-2 md:mb-3">{getLocalizedText(project.description, project.description_en)}</p>
                  <Link
                    to={`/projects/${project.id}`}
                    className="text-sm md:text-base text-gray-900 inline-block border-b border-gray-900"
                  >
                    {i18n.language === 'en' ? 'View Details' : '查看详情'}
                  </Link>
                </div>
              ))
            ) : (
              // 如果没有项目数据，显示占位符
              Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="group">
                  <div className="relative aspect-[4/3] bg-gray-100 mb-3 md:mb-4"></div>
                  <h3 className="text-lg md:text-xl font-medium mb-1 md:mb-2">
                    {i18n.language === 'en' ? 'China Construction Technology Exhibition' : '中国建筑科技展'}
                  </h3>
                  <p className="text-sm md:text-base text-gray-500 line-clamp-2 mb-2 md:mb-3">
                    {i18n.language === 'en' 
                      ? 'Product and technical facility testing and professional recommendations, more quality product recommendations here.'
                      : '产品和技术设施检测及专业推荐，更多的产品优质推荐于此处。'
                    }
                  </p>
                  <span className="text-sm md:text-base text-gray-900 relative">
                    <span className="relative">
                      {i18n.language === 'en' ? 'View Details' : '查看详情'}
                      <span className="absolute left-0 bottom-0 w-full h-[1px] bg-gray-400" />
                    </span>
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section ref={contactRef} data-section="contact" className="py-12 md:py-20 bg-gray-50">
        <div className="mx-auto px-4 md:px-12 max-w-[2000px]">
          {/* Header with underline */}
          <div className="relative mb-12 md:mb-24">
            <div className="flex justify-between items-center">
              <Link 
                to="/contact" 
                className="text-2xl md:text-3xl font-bold flex items-center hover:text-gray-700 transition-colors"
              >
                <span>{i18n.language === 'en' ? 'CONTACT US' : '联系我们'}</span>
                <span className="ml-2 transform group-hover:translate-x-1 transition-transform">→</span>
              </Link>
            </div>
            <div className="absolute -bottom-4 md:-bottom-6 left-0 right-0 h-[1px] bg-gray-300" />
          </div>

          {/* Contact Grid */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <div className="bg-white flex flex-col md:flex-row h-auto md:h-[414px]">
              <div className="relative w-full md:w-1/2 h-[200px] md:h-auto overflow-hidden md:order-2">
                <img 
                  src={contactImages.office} 
                  alt={i18n.language === 'en' ? 'About Us' : '关于我们'} 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  style={{ height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    e.currentTarget.src = 'https://via.placeholder.com/600x400?text=Office+Interior';
                  }}
                  loading="lazy"
                />
              </div>
              <div className="flex-1 p-4 md:p-8 flex flex-col md:order-1">
                <div>
                  <h3 className="text-sm md:text-base font-normal mb-1 md:mb-2">{i18n.language === 'en' ? 'About Us' : '关于我们'}</h3>
                  <h4 className="text-lg md:text-2xl font-normal mb-2 md:mb-4">{i18n.language === 'en' ? 'Shenzhen Dongda International Engineering Design Co., Ltd.' : '深圳市东大国际工程设计有限公司'}</h4>
                </div>
                <div className="flex-grow"></div>
                <div>
                  <p className="text-xs text-[#676767] mb-[22px]">
                    {i18n.language === 'en' 
                      ? 'Shenzhen Dongda International Engineering Design Co., Ltd. relies on Southeast University. Established in Shenzhen for more than 30 years, it is a learning, research and innovation-oriented national high-tech design enterprise with the dean of Southeast University School of Architecture and Design Research Institute, Academician Chen Yi as an advisor.'
                      : '深圳市东大国际工程设计有限公司依托东南大学， 已立足深圳三十余年，由"东南大学建筑设计研究院深圳分院"改制而来，创始院长为孟建民院士，是一家学习型、研究型、创新型的国家高新技术设计企业。'}
                  </p>
                  <Link 
                    to="/about" 
                    className="inline-block bg-black text-white w-[116px] md:w-[165px] h-[37px] md:h-[54px] flex items-center justify-center text-sm md:text-base font-medium hover:bg-gray-800 transition-colors rounded-[4px]"
                  >
                    {i18n.language === 'en' ? 'Learn More' : '查看更多'}
                  </Link>
                </div>
              </div>
            </div>

            <div className="bg-white flex flex-col md:flex-row h-auto md:h-[414px]">
              <div className="relative w-full md:w-1/2 h-[200px] md:h-auto overflow-hidden md:order-2">
                <img 
                  src={contactImages.meeting} 
                  alt={i18n.language === 'en' ? 'Join Us' : '加入我们'} 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  style={{ height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    e.currentTarget.src = 'https://via.placeholder.com/600x400?text=Meeting+Room';
                  }}
                  loading="lazy"
                />
              </div>
              <div className="flex-1 p-4 md:p-8 flex flex-col md:order-1">
                <div>
                  <h3 className="text-sm md:text-base font-normal mb-1 md:mb-2">{i18n.language === 'en' ? 'Join Us' : '加入我们'}</h3>
                  <h4 className="text-lg md:text-2xl font-normal mb-2 md:mb-4">{i18n.language === 'en' ? 'Strong Atmosphere' : '浓厚的氛围'}</h4>
                </div>
                <div className="flex-grow"></div>
                <div>
                  <p className="text-xs text-[#676767] mb-[22px]">
                    {i18n.language === 'en' 
                      ? 'We always believe that excellent design comes from caring for humanity, pursuing innovation, and valuing collaboration. We firmly believe that when culture becomes the soil, creativity can grow into a forest. We not only construct buildings, but also build a spiritual home where ideals can grow—here, each design story shines with the warmth of the team, and each spatial solution carries a promise for a better life.'
                      : '我们始终坚信，优秀的设计源于对人文的关怀、对创新的追求以及对协作的重视。我们始终相信，当文化成为土壤，创意便能长成森林。我们不仅营造建筑，更在构筑一个让理想生长的精神家园——在这里，每个设计故事都闪耀着团队的温度，每个空间方案都承载着对美好生活的承诺。'}
                  </p>
                  <Link 
                    to="/join" 
                    className="inline-block bg-black text-white w-[116px] md:w-[165px] h-[37px] md:h-[54px] flex items-center justify-center text-sm md:text-base font-medium hover:bg-gray-800 transition-colors rounded-[4px]"
                  >
                    {i18n.language === 'en' ? 'Join Us' : '加入我们'}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home; 